import type { Experiment } from 'types';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useDataControl } from '@/components/DataTable';
import { formatDateTime, formatNumber } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import { EmptyState, FOCUS_RING, MICRO_LABEL_SHAPE, Pager, TableScroll } from '../ConsolePanel';
import { isAwaitingFirstVisitor } from './home-states';

/**
 * The overview table, drawn as a console grid rather than a `<table>`.
 *
 * `useDataControl` still owns sorting and paging — only the markup changed — so
 * the five-row page and the click-to-sort behaviour are the ones this screen has
 * always had. A grid is used because head and body then share one
 * `grid-template-columns`, which is what keeps a number column from collapsing
 * onto two lines on a narrow panel.
 */

const TEMPLATE = '70px minmax(180px,1fr) 100px 150px 150px 150px 110px';
const MIN_WIDTH = 910;

const formatDateField = (value: number | string) => formatDateTime(value) || 'Not Available';

/** Draft is a selection, running is live, deployed is the primary outcome. */
const STATUS_TONE: Record<string, string> = {
  Running: 'text-ok',
  Draft: 'text-dim',
  QA: 'text-sel',
  Deployed: 'text-accent',
};

type ExperimentsTableProps = {
  experiments: Experiment[];
};

export function ExperimentsOverviewTable({ experiments }: ExperimentsTableProps) {
  const dataController = useDataControl<Experiment>(experiments);
  const { data, sortBy, sortKey, sortDirection, currentPage, pageCount, changePage } = dataController;

  if (experiments.length === 0) return null;

  const heads = [
    { label: 'ID', field: 'id' },
    { label: 'Name', field: 'name' },
    { label: 'Sessions', field: 'sessions' },
    { label: 'Starts At', field: 'startsAt' },
    { label: 'Ends At', field: 'endsAt' },
    { label: 'Created', field: 'createdAt' },
    { label: 'Status', field: 'status' },
  ] as const;

  return (
    <>
      <TableScroll minWidth={MIN_WIDTH}>
        {/* Grid rather than <table>, so the roles have to be spelled out — without a
            table/row ancestor, `aria-sort` on a head is simply ignored. */}
        <div role="table" aria-label="Experiments overview">
          <div
            role="row"
            className="bg-panel2 border-line grid items-center gap-3 border-b px-3.5 py-2"
            style={{ gridTemplateColumns: TEMPLATE }}
          >
            {heads.map((head) => (
              <HeadCell
                key={head.field}
                label={head.label}
                field={head.field}
                sortKey={sortKey}
                sortDirection={sortDirection}
                sortBy={sortBy}
              />
            ))}
          </div>

          <div role="rowgroup" className="divide-line divide-y">
            {data?.map((experiment) => (
              <div
                key={experiment.id}
                role="row"
                className="hover:bg-hover grid items-center gap-3 px-3.5 py-2"
                style={{ gridTemplateColumns: TEMPLATE }}
              >
                <span role="cell" className="num text-dim">
                  {experiment.id}
                </span>
                {/* The role goes on a wrapper, not on the anchor: an explicit role
                    replaces the implicit one, so `role="cell"` on the link took the
                    name out of the reader's links list and left the only route from
                    this table to an experiment announced as a plain cell. */}
                <span role="cell" className="min-w-0">
                  <NavLink
                    to={`/experiments/${experiment.id}`}
                    title={experiment.name}
                    className="block truncate text-meta hover:underline"
                  >
                    {experiment.name}
                  </NavLink>
                </span>
                <span role="cell" className="num">
                  {isAwaitingFirstVisitor(experiment) ? (
                    <span className={cn(MICRO_LABEL_SHAPE, 'text-dim')} title="No session recorded yet">
                      Waiting
                    </span>
                  ) : (
                    formatNumber(Number(experiment.sessions ?? 0))
                  )}
                </span>
                <span role="cell" className="num text-dim truncate">
                  {formatDateField(+(experiment.startsAt as number))}
                </span>
                <span role="cell" className="num text-dim truncate">
                  {formatDateField(+(experiment.endsAt as number))}
                </span>
                <span role="cell" className="num text-dim truncate">
                  {formatDateField(+(experiment.createdAt as number))}
                </span>
                <span
                  role="cell"
                  className={cn(MICRO_LABEL_SHAPE, 'truncate', STATUS_TONE[experiment.status ?? ''] ?? 'text-dim')}
                >
                  {experiment.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </TableScroll>

      {!data?.length && (
        <div className="p-3.5">
          <EmptyState>No Data Found.</EmptyState>
        </div>
      )}

      {pageCount > 1 && <Pager currentPage={currentPage} totalPages={pageCount} onChange={changePage} />}
    </>
  );
}

type HeadCellProps = {
  label: string;
  field: Extract<keyof Experiment, string>;
  sortKey: string | null;
  sortDirection: string | null;
  sortBy: (key?: Extract<keyof Experiment, string> | null) => void;
};

/**
 * A sort head has to say three things, and the redesign only said one of them: the
 * static `ArrowUpDown` marked the column as sortable but never which column was
 * sorted, nor which way. `sortDirection` was already on the controller and simply
 * unread, so the caret now resolves to an up or a down arrow on the active column,
 * and `aria-sort` reports the same thing to assistive tech.
 */
const HeadCell = ({ label, field, sortKey, sortDirection, sortBy }: HeadCellProps) => {
  const active = sortKey === field;
  const ascending = sortDirection === 'asc';
  const Caret = !active ? ArrowUpDown : ascending ? ArrowUp : ArrowDown;

  return (
    <div role="columnheader" aria-sort={!active ? 'none' : ascending ? 'ascending' : 'descending'} className="min-w-0">
      <button
        type="button"
        onClick={() => sortBy(field)}
        aria-label={`Sort by ${label}`}
        className={cn(
          'micro-label hover:text-foreground flex min-h-7 w-full min-w-0 items-center gap-1 rounded-sm text-left',
          'transition-colors duration-150',
          FOCUS_RING,
          active && 'text-foreground',
        )}
      >
        <span className="truncate">{label}</span>
        <Caret className="size-3 shrink-0" strokeWidth={1.5} />
      </button>
    </div>
  );
};
